import type { BootstrapData, CollectionAccess, Member, Root, TaskAccess, User } from "../domain/app-types.ts";

export interface BootstrapState {
  user: User;
  personalRootId: string | null;
  taskOnlyRootIds: Set<string>;
  collectionRootIds: Set<string>;
  roots: Root[];
  taskAccess: TaskAccess[];
  collectionAccess: CollectionAccess[];
  outgoingSharedTaskIds: Set<string>;
  directory: Member[];
  activeRoot: string | null;
}

export function normalizeBootstrapState(body: BootstrapData, preferredRoot: string | null): BootstrapState {
  const activeRoots = body.roots.filter(root => !root.archived_at);
  return {
    user: body.user,
    personalRootId: body.personal_root_id || null,
    taskOnlyRootIds: new Set(body.roots.filter(root => root.role === "task_viewer").map(root => root.id)),
    collectionRootIds: new Set(body.roots.filter(root => root.role === "collection_viewer").map(root => root.id)),
    roots: body.roots.map(root => root.role === "task_viewer" || root.role === "collection_viewer" ? { ...root, role: "viewer" } : root),
    taskAccess: body.task_access || [],
    collectionAccess: body.collection_access || [],
    outgoingSharedTaskIds: new Set(body.shared_by_me || []),
    directory: body.directory || [],
    activeRoot: activeRoots.some(root => root.id === preferredRoot) ? preferredRoot : activeRoots[0]?.id ?? null,
  };
}

export interface BootstrapRestoreCapabilities {
  preferredRoot(): string | null;
  applySession(state: BootstrapState): void;
  restoreAccess(state: BootstrapState): void;
  accessScopeKey(): string;
  knownScope(): Promise<string>;
  resetCursor(): Promise<void>;
  persistScope(scope: string): Promise<void>;
  activeRoot(): string | null;
  persistActiveRoot(rootId: string | null): Promise<void>;
  restoreTaskListMode(): Promise<void>;
}

export function createBootstrapRestoreController(capabilities: BootstrapRestoreCapabilities) {
  return {
    async restore(body: BootstrapData): Promise<void> {
      const state = normalizeBootstrapState(body, capabilities.preferredRoot());
      capabilities.applySession(state);
      capabilities.restoreAccess(state);
      const scope = capabilities.accessScopeKey();
      if (scope !== await capabilities.knownScope()) {
        await capabilities.resetCursor();
        await capabilities.persistScope(scope);
      }
      await capabilities.persistActiveRoot(capabilities.activeRoot());
      await capabilities.restoreTaskListMode();
    },
  };
}
